import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import DeleteAlert from '../../Common/Layout/DeleteAlert';

// Initial state for the User form, adapted from your Admin portal
const initialFieldValues = {
    userId: "00000000-0000-0000-0000-000000000000",
    branchId: "00000000-0000-0000-0000-000000000000",
    name: "",
    userName: "", // Will be set from phoneNumber
    password: "",
    email: "",
    phoneNumber: "",
    roleName: "STAFF", // B2B users can only create staff
    isActive: true,
};

const CompanyUsers = () => {
    const { user } = useAuth();

    // State Management
    const [listLoading, setListLoading] = useState(true);
    const [btnLoading, setBtnLoading] = useState(false);
    const [apiError, setApiError] = useState('');

    // Data State
    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]); // For the dropdown in the modal
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});

    // Pagination State
    const [pageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    // Modal State
    const [addShow, setAddShow] = useState(false);
    const [changePasswordShow, setChangePasswordShow] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // --- DATA FETCHING ---

    // Fetch list of users for the table
    const GetUsersData = async (currentPage) => {
        if (!user?.cid) { setListLoading(false); return; }
        setListLoading(true);
        setApiError('');
        try {
            const res = await apiService.get(`${config.GETUSERSBYCOMPANY}/${user.cid}`, pageSize, currentPage);
            setUsers(res.data || []);
            setTotalPages(res.totalPages || 0);
            setTotalRecords(res.totalRecords || 0);
            setPageNumber(res.pageNumber || currentPage);
        } catch (err) {
            setApiError("Failed to fetch users. Please try again.");
            setUsers([]);
        } finally {
            setListLoading(false);
        }
    };

    // Fetch list of branches for the modal dropdown
    const GetBranches = async () => {
        if (!user?.cid) return;
        try {
            // Fetch all branches for the dropdown (assuming API supports high page size for this)
            const res = await apiService.get(`${config.GETBRANCHBYCOMPANY}/${user.cid}`, 1000, 1);
            setBranches(res.data || []);
        } catch (err) {
            console.error("Failed to fetch branches for dropdown", err);
            setBranches([]);
        }
    };

    // Initial data fetch on component mount
    useEffect(() => {
        if (user?.cid) {
            GetUsersData(1);
            GetBranches();
        }
    }, [user]);

    // --- FORM HANDLERS & VALIDATION ---

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: true });
    };

    const validate = (isPasswordChange = false) => {
        let tempErrors = {};
        if (isPasswordChange) {
            tempErrors.password = values.password ? true : false;
        } else {
            tempErrors.branchId = values.branchId !== "00000000-0000-0000-0000-000000000000";
            tempErrors.name = values.name !== "";
            tempErrors.phoneNumber = values.phoneNumber !== "";
            // Only validate password for new users
            if (values.userId === "00000000-0000-0000-0000-000000000000") {
                tempErrors.password = values.password !== "";
            }
        }
        setErrors(tempErrors);
        return Object.values(tempErrors).every(x => x === true);
    };

    const resetForm = () => {
        setValues(initialFieldValues);
        setErrors({});
        setBtnLoading(false);
    };

    // --- CRUD OPERATIONS ---

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setBtnLoading(true);
            setApiError('');
            const formData = {
                ...values,
                companyId: user?.cid,
                userName: values.phoneNumber, // Logic from your admin portal
                isActive: values.isActive === "true" || values.isActive === true,
            };

            try {
                const isNew = formData.userId === "00000000-0000-0000-0000-000000000000";
                if (isNew) {
                    await apiService.create(config.CREATEUSER, formData);
                } else {
                    await apiService.update(config.UPDATEUSER, formData);
                }

                await GetUsersData(pageNumber);
                handleAddClose();

            } catch (err) {
                setApiError("Failed to create or update user.");
            } finally {
                setBtnLoading(false);
            }
        }
    };

    const handleDelete = async () => {
        if (itemToDelete) {
            setApiError('');
            try {
                await apiService.remove(config.DELETEUSER, itemToDelete);
                const newPageNumber = users.length === 1 && pageNumber > 1 ? pageNumber - 1 : pageNumber;
                await GetUsersData(newPageNumber);
            } catch (error) {
                setApiError("Failed to delete user.");
            }
            handleCloseDeleteModal();
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (validate(true)) {
            setBtnLoading(true);
            setApiError('');
            const formData = {
                userId: values.userId,
                // Assuming your API can reset password directly with a "newpassword" field
                newpassword: values.password
            };
            try {
                // Using a 'post' or 'update' method as per your apiService for this endpoint
                await apiService.create(config.CHANGEPASSWORD, formData);
                handleChangePasswordClose();
            } catch (err) {
                setApiError("Failed to change password.");
            } finally {
                setBtnLoading(false);
            }
        }
    };

    // --- MODAL & UI CONTROLS ---

    const handleAddClose = () => { setAddShow(false); resetForm(); };
    const handleAddShow = () => { resetForm(); setAddShow(true); };
    const showEditDetails = (data) => { setValues(data); setAddShow(true); };

    const handleChangePasswordClose = () => { setChangePasswordShow(false); resetForm(); };
    const handleChangePasswordShow = (data) => {
        // Only set the necessary fields for password change
        setValues({ userId: data.userId, password: "" });
        setChangePasswordShow(true);
    };

    const handleOpenDeleteModal = (id) => { setItemToDelete(id); setShowDeleteModal(true); };
    const handleCloseDeleteModal = () => { setItemToDelete(null); setShowDeleteModal(false); };

    const GetPageData = (number) => { if (pageNumber !== number) GetUsersData(number); };

    const renderPageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
        <li className={`page-item ${pageNumber === number ? 'active' : ''}`} key={number}>
            <Link to="#" className="page-link" onClick={() => GetPageData(number)}>{number}</Link>
        </li>
    ));

    const applyErrorClass = (field) => (field in errors && errors[field] === false) ? "is-invalid" : "";

    return (
        <div className="card p-2">
            <div className="me-2 pb-1">
                <div className="row g-4 align-items-center">
                    <div className="col-sm-auto ms-auto">
                        <div className="hstack gap-2">
                            <button type="button" className="btn btn-primary add-btn" onClick={handleAddShow} ><i className="ri-add-line align-bottom me-1" /> Add User</button>
                        </div>
                    </div>
                </div>
            </div>

            {apiError && <div className="alert alert-danger alert-dismissible fade show"><button type="button" className="btn-close" onClick={() => setApiError('')}></button>{apiError}</div>}

            {listLoading ? (
                <div className="text-center p-5"><div className="spinner-border text-primary" /></div>
            ) : (
                <>
                    {users.length > 0 ? (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>S.No</th>
                                            <th>Name</th>
                                            <th>Mobile (Username)</th>
                                            <th>Email</th>
                                            <th>Branch</th>
                                            <th>Status</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((item, index) => (
                                            <tr key={item.userId}>
                                                <td>{((pageNumber - 1) * pageSize) + index + 1}</td>
                                                <td>{item.name}</td>
                                                <td>{item.userName}</td>
                                                <td>{item.email || '-'}</td>
                                                <td>{item.branchName || '-'}</td>
                                                <td>
                                                    <span className={`badge ${item.isActive ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                                        {item.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    <div className="d-flex gap-2 justify-content-end">
                                                        <Link to="#" className="btn btn-sm btn-soft-secondary" onClick={() => handleChangePasswordShow(item)} title="Change Password">
                                                            <i className="ri-key-fill align-bottom" />
                                                        </Link>
                                                        <Link to="#" className="btn btn-sm btn-soft-primary" onClick={() => showEditDetails(item)} title="Edit">
                                                            <i className="ri-pencil-fill align-bottom" />
                                                        </Link>
                                                        <Link to="#" className="btn btn-sm btn-soft-danger" onClick={() => handleOpenDeleteModal(item.userId)} title="Delete">
                                                            <i className="ri-delete-bin-fill align-bottom" />
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                                <div className="flex-shrink-0">
                                    <div className="text-muted">Showing <span className="fw-semibold">{users.length}</span> of <span className="fw-semibold">{totalRecords}</span> Results</div>
                                </div>
                                <ul className="pagination pagination-separated pagination-sm mb-0">
                                    <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`}>
                                        <Link to="#" className="page-link" onClick={() => GetPageData(pageNumber - 1)}>Previous</Link>
                                    </li>
                                    {renderPageNumbers}
                                    <li className={`page-item ${pageNumber >= totalPages ? 'disabled' : ''}`}>
                                        <Link to="#" className="page-link" onClick={() => GetPageData(pageNumber + 1)}>Next</Link>
                                    </li>
                                </ul>
                            </div>
                        </>
                    ) : (
                        <div className="text-center p-5 border rounded"><p className="mb-0 text-muted">No users found.</p></div>
                    )}
                </>
            )}

            {/* Add/Edit User Modal */}
            <Modal show={addShow} onHide={handleAddClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{values.userId === "00000000-0000-0000-0000-000000000000" ? "Add New" : "Edit"} User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Full Name</label>
                                <input type="text" name="name" value={values.name} onChange={handleInputChange} className={`form-control ${applyErrorClass('name')}`} placeholder="Enter user's full name" />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Mobile Number (Username)</label>
                                <input type="text" name="phoneNumber" value={values.phoneNumber} onChange={handleInputChange} className={`form-control ${applyErrorClass('phoneNumber')}`} placeholder="This will be the login username" />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Email Address</label>
                                <input type="email" name="email" value={values.email} onChange={handleInputChange} className="form-control" placeholder="Enter email (optional)" />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Assign to Branch</label>
                                <select name="branchId" value={values.branchId} onChange={handleInputChange} className={`form-select ${applyErrorClass('branchId')}`}>
                                    <option value="00000000-0000-0000-0000-000000000000">Select a Branch</option>
                                    {branches.map(b => <option key={b.branchId} value={b.branchId}>{b.branchName}</option>)}
                                </select>
                            </div>
                            {/* Only show password field for NEW users */}
                            {values.userId === "00000000-0000-0000-0000-000000000000" && (
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Password</label>
                                    <input type="password" name="password" value={values.password} onChange={handleInputChange} className={`form-control ${applyErrorClass('password')}`} placeholder="Create a password" />
                                </div>
                            )}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Status</label>
                                <select name="isActive" value={values.isActive} onChange={handleInputChange} className="form-select">
                                    <option value={true}>Active</option>
                                    <option value={false}>Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="hstack gap-2 justify-content-end mt-3">
                            <button type="button" className="btn btn-light" onClick={handleAddClose}>Cancel</button>
                            <button type="submit" className="btn btn-success" disabled={btnLoading}>{btnLoading ? 'Saving...' : 'Save Changes'}</button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            {/* Change Password Modal */}
            <Modal show={changePasswordShow} onHide={handleChangePasswordClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Change Password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleChangePassword} noValidate>
                        <div className="mb-3">
                            <label className="form-label">New Password</label>
                            <input type="password" name="password" value={values.password} onChange={handleInputChange} className={`form-control ${applyErrorClass('password')}`} placeholder="Enter a strong new password" autoFocus />
                        </div>
                        <div className="hstack gap-2 justify-content-end mt-4">
                            <button type="button" className="btn btn-light" onClick={handleChangePasswordClose}>Cancel</button>
                            <button type="submit" className="btn btn-success" disabled={btnLoading}>{btnLoading ? 'Updating...' : 'Update Password'}</button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
        </div>
    );
};

export default CompanyUsers;